import { Fragment, useState, useMemo, useCallback } from "react";
import { ListChecks, Eye, Check, X, ArrowUpDown, ArrowDown, ArrowUp } from "lucide-react";
import QuestionPreviewModal from "../../VueQuestion/QuestionPreviewModal";
import { buildPreviewContenu } from "../../../utils/questionUtils";
import { DOMAINES, domaineDuType } from "./margeDuHasard";
import { nomDuTexte } from "../EvalNat/evaluationConfig";
import { Pastille, PARTIES, PARTIE_NEUTRE } from "./ElementsProgression";

/**
 * Extraire le score d'un élève pour une question donnée.
 * Scanne toutes les catégories de reponses car on ne sait pas
 * dans quelle catégorie se trouve la question.
 * Retourne { score, reponse } ou null si pas répondu.
 */
function getEleveScore(eleveReponses, questionId) {
  if (!eleveReponses) return null;
  for (const cat of Object.values(eleveReponses)) {
    if (cat && cat[questionId] !== undefined) {
      const r = cat[questionId];
      return { score: r.score, reponse: r.réponse || r.reponse };
    }
  }
  return null;
}

const numero = (questionNum) => parseInt(String(questionNum ?? '').replace(/\D/g, ''), 10) || 0;
const couleurTaux = (taux) => (taux >= 70 ? 'text-emerald-600' : taux >= 40 ? 'text-amber-600' : 'text-red-500');

/**
 * Les colonnes comme la grille d'EvalDetail (12/10/2026) : rangées par partie (l'ordre des domaines de l'évaluation
 * nationale, un texte de compréhension par partie), puis par numéro ; un en-tête par item (son intitulé), ses
 * variantes dessous (même étiquette), comme les compétences sous un attendu. Avant, l'ordre du back : Q02 à Q22,
 * les problèmes, puis Q01, Q06 et les variantes de Q03, deux « Q01 » côte à côte.
 */
const rangerParPartie = (questions) => {
  const parties = new Map();
  (questions || []).forEach((q, i) => {
    const domaine = domaineDuType(q.type_label);
    const cle = !domaine ? `autre|${q.type_label || ''}` : domaine.cle === 'comprehension' ? `comprehension|${q.type_label}` : domaine.cle;
    if (!parties.has(cle)) {
      parties.set(cle, {
        cle,
        nom: !domaine ? (q.type_label || 'Questions') : domaine.cle === 'comprehension' ? nomDuTexte(q.type_label) : domaine.nom,
        ordre: domaine ? DOMAINES.indexOf(domaine) : DOMAINES.length,
        style: domaine ? PARTIES[domaine.cle] : PARTIE_NEUTRE,
        items: new Map(),
      });
    }
    const partie = parties.get(cle);
    const cleItem = q.label_id ?? `${q.type_label}|${q.question_num ?? q.id}`;
    if (!partie.items.has(cleItem)) {
      partie.items.set(cleItem, { cle: `${cle}#${cleItem}`, num: q.question_num || `Q${i + 1}`, libelle: q.nom_label || '', questions: [] });
    }
    partie.items.get(cleItem).questions.push(q);
  });
  return [...parties.values()]
    .sort((a, b) => a.ordre - b.ordre || a.nom.localeCompare(b.nom, 'fr'))
    .map((p) => ({ ...p, items: [...p.items.values()].sort((a, b) => numero(a.num) - numero(b.num)) }));
};

/**
 * Tableau élèves × questions — vue grille de résultats
 */
function RecapQuestionsEval({ questions, elevesReponses }) {
  // Le tri : par nom, par score, ou par un item (ceux qui l'ont faux d'abord, puis l'inverse)
  const [tri, setTri] = useState({ par: 'nom' });
  const [previewQuestion, setPreviewQuestion] = useState(null);

  // Élèves qui ont répondu uniquement
  const elevesAvecReponses = useMemo(() => {
    if (!Array.isArray(elevesReponses)) return [];
    return elevesReponses.filter(e => e.reponses);
  }, [elevesReponses]);

  const parties = useMemo(() => rangerParPartie(questions), [questions]);
  const colonnes = useMemo(() => parties.flatMap((p) => p.items.flatMap((item) => item.questions)), [parties]);
  const items = useMemo(() => new Map(parties.flatMap((p) => p.items.map((item) => [item.cle, { ...item, partie: p }]))), [parties]);

  // Grille de scores : { eleveId → { questionId → { score, reponse } } }
  // + score total par élève + taux par question
  const { grille, scoresEleves, tauxQuestions } = useMemo(() => {
    const g = new Map();
    const totaux = new Map(); // eleveId → { correct, total }
    const qStats = new Map(); // questionId → { correct, total }

    // Init question stats
    (questions || []).forEach(q => qStats.set(q.id, { correct: 0, total: 0 }));

    elevesAvecReponses.forEach(eleve => {
      const id = eleve.eleve_id || eleve.id_eleve;
      const row = {};
      let correct = 0;
      let total = 0;

      (questions || []).forEach(q => {
        const result = getEleveScore(eleve.reponses, q.id);
        row[q.id] = result;
        if (result) {
          total++;
          if (result.score === 1) {
            correct++;
            qStats.get(q.id).correct++;
          }
          qStats.get(q.id).total++;
        }
      });

      g.set(id, row);
      totaux.set(id, { correct, total });
    });

    return { grille: g, scoresEleves: totaux, tauxQuestions: qStats };
  }, [questions, elevesAvecReponses]);

  // Tri des élèves
  const sortedEleves = useMemo(() => {
    const parNom = (a, b) => (a.nom_eleve || '').localeCompare(b.nom_eleve || '', 'fr');
    const list = [...elevesAvecReponses].sort(parNom);
    if (tri.par === 'score') {
      // plus faibles en premier
      list.sort((a, b) => (scoresEleves.get(a.eleve_id || a.id_eleve)?.correct || 0) - (scoresEleves.get(b.eleve_id || b.id_eleve)?.correct || 0));
    } else if (tri.par === 'item' && items.has(tri.cle)) {
      // La part de ses variantes justes ; sans réponse, à la fin
      const valeur = (eleve) => {
        const row = grille.get(eleve.eleve_id || eleve.id_eleve) || {};
        const reponses = items.get(tri.cle).questions.map((q) => row[q.id]).filter(Boolean);
        return reponses.length ? reponses.filter((r) => r.score === 1).length / reponses.length : null;
      };
      const valeurs = new Map(list.map((e) => [e, valeur(e)]));
      list.sort((a, b) => {
        const va = valeurs.get(a);
        const vb = valeurs.get(b);
        if (va === null || vb === null) return va === vb ? 0 : (va === null ? 1 : -1);
        return (va - vb) * tri.sens;
      });
    }
    return list;
  }, [elevesAvecReponses, tri, scoresEleves, items, grille]);

  // Preview — transformation unifiée via buildPreviewContenu
  const handlePreview = useCallback((q) => {
    setPreviewQuestion(buildPreviewContenu(q));
  }, []);

  const toggleSort = useCallback(() => {
    setTri((prev) => (prev.par === 'nom' ? { par: 'score' } : { par: 'nom' }));
  }, []);

  // Un clic sur un item : ceux qui l'ont faux d'abord ; un second clic : ceux qui l'ont juste d'abord
  const trierParItem = useCallback((cle) => {
    setTri((prev) => (prev.par === 'item' && prev.cle === cle ? { par: 'item', cle, sens: -prev.sens } : { par: 'item', cle, sens: 1 }));
  }, []);

  const totalQuestions = questions?.length || 0;

  // Le taux de réussite de la classe, toutes questions confondues
  const globalTaux = useMemo(() => {
    let totalCorrect = 0, totalAll = 0;
    tauxQuestions.forEach(s => { totalCorrect += s.correct; totalAll += s.total; });
    return totalAll > 0 ? Math.round((totalCorrect / totalAll) * 100) : 0;
  }, [tauxQuestions]);

  if (totalQuestions === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-medium">Aucune question dans cette évaluation</p>
      </div>
    );
  }

  if (elevesAvecReponses.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="font-medium">Aucun élève n'a encore répondu</p>
      </div>
    );
  }

  const itemTrie = tri.par === 'item' ? items.get(tri.cle) : null;
  const libelleTri = tri.par === 'score'
    ? 'Trié par score'
    : itemTrie
      ? `Trié par ${itemTrie.partie.nom} ${itemTrie.num}`
      : 'Ordre alphabétique';
  const estTrie = (q) => Boolean(itemTrie && itemTrie.questions.includes(q));

  return (
    <div className="px-4 sm:px-0 py-4 space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-xs text-gray-500">
          {elevesAvecReponses.length} élève{elevesAvecReponses.length > 1 ? 's' : ''} · {totalQuestions} question{totalQuestions > 1 ? 's' : ''}
        </p>
        <p className="text-[11px] font-bold text-slate-400">Cliquez une question pour trier les élèves</p>
        <button
          onClick={toggleSort}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition"
        >
          <ArrowUpDown size={13} />
          {tri.par === 'nom' ? 'Trier par score' : 'Trier par nom'}
        </button>
      </div>

      {/* Tableau, sur le principe de la grille d'EvalDetail (12/10/2026) : les colonnes rangées par partie puis par
          item, l'intitulé de l'item en en-tête (à la verticale, pour que 31 questions tiennent en largeur), ses
          variantes dessous ; sous chaque colonne, la réussite de la classe, toujours visible en bas de la zone ;
          le score de chaque élève juste après son nom, fixes à gauche ; un clic sur un item trie les élèves. Fonds
          pleins et bordures sur les cellules : une cellule fixe laisse voir ce qui passe dessous. */}
      <div className="overflow-auto max-h-[65vh] bg-white border-2 border-[#D5E6F3] rounded-2xl shadow-sm">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-20">
            <tr>
              {/* Nom et score : fixes à gauche */}
              <th rowSpan={3} className="sticky left-0 z-30 bg-white px-4 pb-2 text-left align-bottom w-[160px] min-w-[160px] max-w-[160px] border-b-2 border-b-[#D5E6F3]">
                <div className="text-xs font-extrabold text-slate-500">Élève</div>
                <div className="text-[11px] font-bold text-slate-400 mt-0.5 truncate">{libelleTri}</div>
              </th>
              <th rowSpan={3} className="sticky left-[160px] z-30 bg-white px-2 pb-2 text-center align-bottom w-[68px] min-w-[68px] border-b-2 border-b-[#D5E6F3] border-r border-r-[#E8F3FB]">
                <span className="text-[10px] font-extrabold text-slate-500 uppercase">Score</span>
              </th>
              {/* Les parties */}
              {parties.map((p) => (
                <th key={p.cle} scope="colgroup" colSpan={p.items.reduce((n, item) => n + item.questions.length, 0)} className="bg-white px-1 pt-2 pb-1 text-left border-b border-[#E8F3FB]">
                  <Pastille partie={p.style} nom={p.nom} petite />
                </th>
              ))}
            </tr>
            <tr>
              {/* Les items : leur intitulé, un clic pour trier */}
              {parties.map((p) => p.items.map((item) => {
                const actif = itemTrie && itemTrie.cle === item.cle;
                const Fleche = actif ? (tri.sens === 1 ? ArrowDown : ArrowUp) : null;
                return (
                  <th key={item.cle} scope="colgroup" colSpan={item.questions.length} className={`px-0.5 pt-1 align-bottom border-b border-[#E8F3FB] ${actif ? 'bg-[#EAF4FB]' : 'bg-white'}`}>
                    <button
                      type="button"
                      onClick={() => trierParItem(item.cle)}
                      title={`${item.num} · ${item.libelle}`}
                      aria-label={`Trier par ${p.nom} ${item.num}`}
                      className="mx-auto flex flex-col items-center gap-1 rounded-md px-0.5 py-1 hover:bg-[#F2F8FC]"
                    >
                      {/* sideways : sinon l'apostrophe typographique reste debout (« moitié d · un ») */}
                      {item.libelle ? (
                        <span className="[writing-mode:vertical-rl] [text-orientation:sideways] rotate-180 max-h-[8.5rem] overflow-hidden text-ellipsis whitespace-nowrap text-[10.5px] font-bold leading-none text-slate-600">
                          {item.libelle}
                        </span>
                      ) : null}
                      <span className="whitespace-nowrap text-[10px] font-extrabold text-slate-800">
                        {item.num}{item.questions.length > 1 ? <span className="text-slate-400"> ×{item.questions.length}</span> : null}
                      </span>
                      {Fleche ? <Fleche size={11} className="text-cyan-600" aria-hidden="true" /> : null}
                    </button>
                  </th>
                );
              }))}
            </tr>
            <tr>
              {/* Chaque question : son aperçu */}
              {colonnes.map((q) => (
                <th key={q.id} className={`px-0.5 pb-1 text-center w-[34px] min-w-[34px] border-b-2 border-[#D5E6F3] ${estTrie(q) ? 'bg-[#EAF4FB]' : 'bg-white'}`}>
                  <button
                    onClick={() => handlePreview(q)}
                    className="w-6 h-6 mx-auto rounded-md flex items-center justify-center text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 transition"
                    title="Aperçu élève"
                  >
                    <Eye size={12} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedEleves.map((eleve, rowIdx) => {
              const eleveId = eleve.eleve_id || eleve.id_eleve;
              const row = grille.get(eleveId) || {};
              const scores = scoresEleves.get(eleveId) || { correct: 0, total: 0 };
              const pct = scores.total > 0 ? Math.round((scores.correct / scores.total) * 100) : 0;
              const fond = rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

              return (
                <tr key={eleveId} className={fond}>
                  {/* Nom — fixe */}
                  <td
                    className={`sticky left-0 z-10 ${fond} px-4 py-2.5 text-sm font-medium text-gray-700 truncate w-[160px] min-w-[160px] max-w-[160px] border-b border-gray-100`}
                    title={eleve.nom_eleve}
                  >
                    {eleve.nom_eleve}
                  </td>
                  {/* Score — fixe, juste après le nom */}
                  <td className={`sticky left-[160px] z-10 ${fond} px-2 py-2.5 text-center border-b border-gray-100 border-r border-r-[#E8F3FB]`}>
                    <div className="flex flex-col items-center">
                      <span className={`text-sm font-bold ${couleurTaux(pct)}`}>
                        {scores.correct}/{scores.total}
                      </span>
                      <span className="text-[10px] text-gray-400">{pct}%</span>
                    </div>
                  </td>
                  {/* Cellules résultats */}
                  {colonnes.map(q => {
                    const result = row[q.id];
                    const teinte = estTrie(q) ? 'bg-[#EAF4FB]/60' : '';
                    if (!result) {
                      return (
                        <td key={q.id} className={`px-0.5 py-2.5 text-center border-b border-gray-100 ${teinte}`}>
                          <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center mx-auto">
                            <span className="text-gray-300 text-[10px]">—</span>
                          </div>
                        </td>
                      );
                    }
                    const isCorrect = result.score === 1;
                    return (
                      <td key={q.id} className={`px-0.5 py-2.5 text-center border-b border-gray-100 ${teinte}`}>
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center mx-auto ${
                          isCorrect
                            ? 'bg-emerald-100 text-emerald-600'
                            : 'bg-red-100 text-red-500'
                        }`}>
                          {isCorrect
                            ? <Check size={13} strokeWidth={3} />
                            : <X size={13} strokeWidth={3} />
                          }
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {/* La classe, sous chaque colonne : toujours visible en bas de la zone */}
          <tfoot>
            <tr>
              <td className="sticky bottom-0 left-0 z-30 bg-white px-4 py-2 text-left text-[11.5px] font-extrabold text-slate-400 whitespace-nowrap border-t-2 border-t-[#D5E6F3]">
                Classe ({elevesAvecReponses.length})
              </td>
              <td className="sticky bottom-0 left-[160px] z-30 bg-white px-2 py-2 text-center border-t-2 border-t-[#D5E6F3] border-r border-r-[#E8F3FB]">
                <span className={`text-xs font-bold ${couleurTaux(globalTaux)}`}>{globalTaux}%</span>
              </td>
              {colonnes.map((q) => {
                const stats = tauxQuestions.get(q.id) || { correct: 0, total: 0 };
                const taux = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
                return (
                  <td key={q.id} className="sticky bottom-0 z-20 bg-white px-0.5 pt-1.5 pb-2 text-center border-t-2 border-t-[#D5E6F3]" title={`${stats.correct}/${stats.total} justes`}>
                    {stats.total > 0 ? (
                      <Fragment>
                        <div className="mx-auto mb-1 flex h-1.5 w-6 overflow-hidden rounded-full bg-red-200">
                          <span className="h-full bg-emerald-500" style={{ width: `${taux}%` }} />
                        </div>
                        <span className={`text-[10px] font-extrabold ${couleurTaux(taux)}`}>{taux}%</span>
                      </Fragment>
                    ) : <span className="text-[10px] text-gray-300">—</span>}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Preview modal */}
      {previewQuestion && (
        <QuestionPreviewModal
          question={previewQuestion}
          onClose={() => setPreviewQuestion(null)}
        />
      )}
    </div>
  );
}

export default RecapQuestionsEval;
